/**
 * 利用規約（公開ページ・ログイン不要）
 *
 * 本文の出典: docs/OWNER_APP_LEGAL_DRAFT.md 第 1 部
 *
 * 注意: 法的に有効な「正本」は日本語。UI 言語に関わらず本文は日本語で表示する。
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const COMPANY_NAME = "株式会社 Naturenix";
const SERVICE_NAME = "nx-connect";
const SUPPORT_EMAIL = "support@naturenix.co.jp";
const JURISDICTION = "東京地方裁判所";
const LAST_UPDATED = "2026-04-22";

export default function TermsPage() {
  const { i18n, t } = useTranslation();
  const isJa = i18n.language?.startsWith("ja") ?? true;

  return (
    <article className="nx-legal">
      <header className="nx-legal__header">
        <Link to="/login" className="nx-btn-ghost nx-legal__back">
          ← {t("common.back")}
        </Link>
        <h1>利用規約</h1>
        <p className="nx-text-secondary nx-legal__meta">最終更新: {LAST_UPDATED}</p>
        {!isJa && (
          <p className="nx-legal__notice">
            This page is the official Japanese version of our Terms of Service.
            An English translation is not provided; please consult a Japanese-speaker
            if needed. Application UI is bilingual but the legal text is Japanese.
          </p>
        )}
      </header>

      <section>
        <h2>第 1 条（適用）</h2>
        <ol>
          <li>
            本規約は、{COMPANY_NAME}（以下「当社」）が提供するモニタリングサービス
            「{SERVICE_NAME}」（以下「本サービス」）の利用条件を定めるものです。
          </li>
          <li>
            本サービスを利用する顧客（以下「利用者」）は、本規約に同意のうえ、
            本サービスを利用するものとします。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 2 条（定義）</h2>
        <ul>
          <li>
            <strong>対象機器</strong>: 当社が販売するポータブル電源等のうち、
            本サービスの対象として当社が指定する製品
          </li>
          <li>
            <strong>テレメトリ</strong>: 対象機器が送信する稼働状態に関する情報
            （充電残量、電圧、温度、ポート状態、サイクル数、エラー情報、概略位置情報等）
          </li>
          <li>
            <strong>アカウント</strong>: 利用者が本サービスを利用するために
            登録した電子メールアドレスに紐づく利用権
          </li>
        </ul>
      </section>

      <section>
        <h2>第 3 条（利用資格）</h2>
        <ol>
          <li>
            本サービスは、当社または当社の販売代理店を通じて対象機器を購入し、
            利用者登録（保証登録）を完了した法人または個人事業主に提供されます。
          </li>
          <li>
            当社は、利用者が以下に該当する場合、利用を断ることがあります。
            <ul>
              <li>反社会的勢力との関係が認められる場合</li>
              <li>過去に本規約違反があった場合</li>
              <li>その他、当社が不適切と判断した場合</li>
            </ul>
          </li>
        </ol>
      </section>

      <section>
        <h2>第 4 条（アカウント登録と認証）</h2>
        <ol>
          <li>
            利用者は、対象機器の保証登録時に当社に届け出た電子メールアドレスを使用して
            本サービスにログインします。
          </li>
          <li>
            ログインは、利用者が入力した電子メールアドレスに送付される 6 桁の
            ワンタイムパスワード（OTP）により認証されます。
          </li>
          <li>利用者は、OTP を第三者に開示してはなりません。</li>
          <li>
            利用者は、登録した電子メールアドレスのセキュリティを自己の責任において
            管理するものとします。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 5 条（提供する機能）</h2>
        <ol>
          <li>
            本サービスは、以下の機能を提供します。
            <ul>
              <li>利用者が所有する対象機器の現在の稼働状態の表示</li>
              <li>過去の稼働履歴のグラフ表示</li>
              <li>異常発生時のメール・プッシュ通知</li>
              <li>保証書 PDF の閲覧・ダウンロード</li>
              <li>利用者の登録情報の確認</li>
            </ul>
          </li>
          <li>
            本サービスは<strong>監視（モニタリング）のみ</strong>を目的とし、
            対象機器の遠隔操作（ポートの ON/OFF 等）は提供しません。
          </li>
          <li>当社は、本サービスの内容を予告なく変更することがあります。</li>
        </ol>
      </section>

      <section>
        <h2>第 6 条（複数機器の取り扱い）</h2>
        <ol>
          <li>
            同一の電子メールアドレスで複数の対象機器を保証登録している場合、
            利用者は本サービスにて当該機器を一括して閲覧できます。
          </li>
          <li>
            異なる電子メールアドレスで登録された機器を統合して閲覧することはできません
            （ただし、当社のサポート窓口にてアカウント統合を依頼することができます）。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 7 条（譲渡）</h2>
        <ol>
          <li>
            対象機器を第三者に譲渡する場合、利用者は当社のサポート窓口にて
            所有者変更手続きを行うものとします。
          </li>
          <li>
            譲渡完了後、譲渡前の利用者は本サービス上で当該機器を閲覧できなくなります。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 8 条（禁止事項）</h2>
        <p>利用者は、本サービスの利用にあたって、以下の行為を行ってはなりません。</p>
        <ol>
          <li>法令または公序良俗に反する行為</li>
          <li>当社または第三者の権利・利益を侵害する行為</li>
          <li>
            本サービスの運営を妨害する行為（不正アクセス、過度な API リクエスト、
            リバースエンジニアリング等）
          </li>
          <li>自己以外の者に対するなりすまし</li>
          <li>
            本サービスを通じて取得した情報の第三者提供
            （自己の業務上の合理的な範囲を除く）
          </li>
        </ol>
      </section>

      <section>
        <h2>第 9 条（サービスの中断・停止）</h2>
        <ol>
          <li>
            当社は、以下の場合、利用者への事前通知なく本サービスを中断することがあります。
            <ul>
              <li>システム保守・更新が必要な場合</li>
              <li>火災・停電・天災等の不可抗力により提供が困難な場合</li>
              <li>その他、運用上または技術上当社が必要と判断した場合</li>
            </ul>
          </li>
          <li>
            当社は、本条による中断・停止により利用者または第三者に生じた損害について
            責任を負いません。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 10 条（データの取り扱い）</h2>
        <ol>
          <li>
            当社は、本サービスの提供に必要な範囲で利用者の個人情報およびテレメトリを
            取得・利用します。
          </li>
          <li>
            詳細は別途定める<Link to="/privacy">プライバシーポリシー</Link>に従います。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 11 条（保証範囲とデータの正確性）</h2>
        <ol>
          <li>
            本サービスにて表示されるテレメトリは、対象機器が送信したデータに基づきます。
            通信状態・センサー精度等によりリアルタイム性および正確性に限界があることを、
            利用者は了承するものとします。
          </li>
          <li>
            当社は、本サービスの中断、データの欠落、表示の遅延等により利用者または第三者に
            生じた損害について、当社に故意または重大な過失がある場合を除き、責任を負いません。
          </li>
          <li>
            通知機能（メール・プッシュ通知）は、利用者の端末またはネットワーク状況により
            遅延または届かない場合があります。当社は通知の到達を保証しません。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 12 条（知的財産権）</h2>
        <p>
          本サービスおよび本サービスを通じて提供されるすべてのコンテンツに関する
          知的財産権は、当社または当社にライセンスを許諾している第三者に帰属します。
        </p>
      </section>

      <section>
        <h2>第 13 条（退会）</h2>
        <ol>
          <li>利用者は、当社所定の手続きにより本サービスから退会することができます。</li>
          <li>
            退会後、利用者の個人情報は本書プライバシーポリシーに従い処理されます。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 14 条（利用規約の変更）</h2>
        <p>
          当社は、必要と判断した場合、本規約を変更することがあります。
          変更後の規約は、本サービスにて掲示した時点から効力を生じます。
        </p>
      </section>

      <section>
        <h2>第 15 条（準拠法・管轄裁判所）</h2>
        <ol>
          <li>本規約は日本法に準拠します。</li>
          <li>
            本サービスに関連して紛争が生じた場合、{JURISDICTION}を専属的合意管轄裁判所とします。
          </li>
        </ol>
      </section>

      <section>
        <h2>お問い合わせ</h2>
        <address className="nx-legal__address">
          {COMPANY_NAME}<br />
          電子メール: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </address>
      </section>

      <footer className="nx-legal__footer">
        <Link to="/privacy" className="nx-btn-ghost">プライバシーポリシーを見る →</Link>
        <Link to="/login" className="nx-btn-ghost">ログイン画面に戻る</Link>
      </footer>
    </article>
  );
}
